import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import QuickActions from '../components/QuickActions';
import { useTeam } from '../contexts/TeamContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Heart, Home, Users, Stethoscope, Sparkles, Share2, Phone, Save } from 'lucide-react';

const API_URL = window.location.origin || '';

const EndOfLife = () => {
  const { currentTeam } = useTeam();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    care_environment: {
      location_preference: '',
      backup_location: '',
      facility_preferences: '',
      people_present: '',
      people_not_present: '',
      special_visitors: '',
      pet_considerations: '',
      music_preferences: '',
      lighting: '',
      temperature: '',
      scents: '',
      room_setup: '',
      pain_management: '',
      comfort_measures: '',
      touch_preferences: '',
      spiritual_care: '',
      alternative_therapies: ''
    },
    medical_wishes: {
      dnr_dni: '',
      intubation: '',
      feeding_tube: '',
      dialysis: '',
      blood_transfusion: '',
      resuscitation: '',
      organ_donation: '',
      body_donation: ''
    },
    life_celebration: {
      event_type: '',
      timing: '',
      duration: '',
      venue: '',
      indoor_outdoor: '',
      accessibility: '',
      guest_list: '',
      must_invite: '',
      children_welcome: '',
      food_preferences: '',
      catering_type: '',
      music: '',
      activities: '',
      theme_colors: '',
      decorations: '',
      photos: '',
      special_elements: '',
      budget: ''
    },
    comfort_symptom: {
      pain_tolerance: '',
      medication_preferences: '',
      alternative_pain_relief: '',
      symptom_concerns: '',
      quality_vs_longevity: '',
      sleep_preferences: '',
      bathing_preferences: '',
      grooming_preferences: '',
      clothing_preferences: '',
      mobility_aids: ''
    },
    spiritual_emotional: {
      religious_affiliation: '',
      clergy_contact: '',
      prayer_preferences: '',
      sacred_texts: '',
      religious_objects: '',
      counseling_preferences: '',
      emotional_support: '',
      legacy_work: '',
      messages_to_record: ''
    },
    information_sharing: {
      core_team_info: '',
      support_team_info: '',
      children_info: '',
      social_media: '',
      update_frequency: '',
      spokesperson: '',
      visiting_hours: ''
    },
    providers: {
      preferred_hospice: '',
      hospice_type: '',
      preferred_physicians: '',
      care_team: '',
      backup_caregiver: '',
      emergency_contacts: ''
    }
  });

  useEffect(() => {
    if (currentTeam?.id) {
      fetchPreferences();
    }
  }, [currentTeam?.id]); // Only re-run when team ID changes

  const fetchPreferences = async () => {
    try {
      // Only show loading on initial load
      if (Object.values(preferences.care_environment).every(v => v === '')) {
        setLoading(true);
      }
      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/end-of-life-preferences`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data && data.care_preferences) {
          setPreferences(prev => ({
            ...prev,
            ...JSON.parse(data.care_preferences)
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    try {
      setSaving(true);
      const response = await fetch(`${API_URL}/api/teams/${currentTeam.id}/end-of-life-preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          care_preferences: JSON.stringify(preferences)
        })
      });

      if (response.ok) {
        toast.success('Preferences saved successfully');
      } else {
        toast.error('Failed to save preferences');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Error saving preferences');
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (section, field, value) => {
    setPreferences(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  if (!currentTeam) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto fade-in">
          <h1 className="text-4xl font-bold mb-6">End of Life Planning</h1>
          <Card className="p-8">
            <p className="text-gray-600">Please select a team to manage end of life preferences.</p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto fade-in">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-4xl font-bold">End of Life Planning</h1>
            <p className="text-gray-600 mt-2">Plan ahead with dignity and clarity</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={savePreferences} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save All'}
            </Button>
            <QuickActions position="top" />
          </div>
        </div>

        {loading ? (
          <Card className="p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading preferences...</p>
          </Card>
        ) : (
          <Tabs defaultValue="care-environment" className="space-y-6">
            <TabsList className="grid grid-cols-4 md:grid-cols-7 gap-2">
              <TabsTrigger value="care-environment">
                <Home className="w-4 h-4 mr-2" />
                Care Environment
              </TabsTrigger>
              <TabsTrigger value="medical-wishes">
                <Stethoscope className="w-4 h-4 mr-2" />
                Medical Wishes
              </TabsTrigger>
              <TabsTrigger value="life-celebration">
                <Sparkles className="w-4 h-4 mr-2" />
                Life Celebration
              </TabsTrigger>
              <TabsTrigger value="comfort">
                <Heart className="w-4 h-4 mr-2" />
                Comfort
              </TabsTrigger>
              <TabsTrigger value="spiritual">
                <Heart className="w-4 h-4 mr-2" />
                Spiritual
              </TabsTrigger>
              <TabsTrigger value="sharing">
                <Share2 className="w-4 h-4 mr-2" />
                Information
              </TabsTrigger>
              <TabsTrigger value="providers">
                <Phone className="w-4 h-4 mr-2" />
                Providers
              </TabsTrigger>
            </TabsList>

            {/* Care Environment Tab */}
            <TabsContent value="care-environment">
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-6">Care Environment Preferences</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Location Preferences</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Where do you want to be?</Label>
                        <select
                          value={preferences.care_environment.location_preference}
                          onChange={(e) => updateSection('care_environment', 'location_preference', e.target.value)}
                          className="w-full border-2 rounded-md px-3 py-2 mt-1"
                        >
                          <option value="">Select preference...</option>
                          <option value="home">Home</option>
                          <option value="hospital">Hospital</option>
                          <option value="hospice_facility">Hospice Facility</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <Label>Backup location if first choice isn't possible</Label>
                        <Input
                          value={preferences.care_environment.backup_location}
                          onChange={(e) => updateSection('care_environment', 'backup_location', e.target.value)}
                          placeholder="Backup location"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Specific facility preferences (if institutional care needed)</Label>
                        <Textarea
                          value={preferences.care_environment.facility_preferences}
                          onChange={(e) => updateSection('care_environment', 'facility_preferences', e.target.value)}
                          placeholder="Name specific facilities, neighborhoods, or requirements"
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">People & Presence</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Who do you want present?</Label>
                        <Textarea
                          value={preferences.care_environment.people_present}
                          onChange={(e) => updateSection('care_environment', 'people_present', e.target.value)}
                          placeholder="List specific people or roles (e.g., immediate family, close friends)"
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Who do you specifically NOT want present?</Label>
                        <Textarea
                          value={preferences.care_environment.people_not_present}
                          onChange={(e) => updateSection('care_environment', 'people_not_present', e.target.value)}
                          placeholder="It's okay to specify"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Special visitors you'd like contacted</Label>
                        <Textarea
                          value={preferences.care_environment.special_visitors}
                          onChange={(e) => updateSection('care_environment', 'special_visitors', e.target.value)}
                          placeholder="Friends from different life chapters, former colleagues, etc."
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Pet considerations</Label>
                        <Textarea
                          value={preferences.care_environment.pet_considerations}
                          onChange={(e) => updateSection('care_environment', 'pet_considerations', e.target.value)}
                          placeholder="Do you want your pets present? Visiting pets? Therapy animals?"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Environment Settings</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Music preferences</Label>
                        <Textarea
                          value={preferences.care_environment.music_preferences}
                          onChange={(e) => updateSection('care_environment', 'music_preferences', e.target.value)}
                          placeholder="Specific songs, artists, playlists, or genres. Spotify/Apple Music links welcome."
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Lighting</Label>
                          <select
                            value={preferences.care_environment.lighting}
                            onChange={(e) => updateSection('care_environment', 'lighting', e.target.value)}
                            className="w-full border-2 rounded-md px-3 py-2 mt-1"
                          >
                            <option value="">Select...</option>
                            <option value="bright">Bright</option>
                            <option value="dim">Dim</option>
                            <option value="natural">Natural light</option>
                            <option value="no_preference">No preference</option>
                          </select>
                        </div>
                        <div>
                          <Label>Temperature</Label>
                          <Input
                            value={preferences.care_environment.temperature}
                            onChange={(e) => updateSection('care_environment', 'temperature', e.target.value)}
                            placeholder="Warm, cool, specific temp"
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div>
                        <Label>Scent preferences</Label>
                        <Input
                          value={preferences.care_environment.scents}
                          onChange={(e) => updateSection('care_environment', 'scents', e.target.value)}
                          placeholder="Aromatherapy, fresh air, no scents, specific scents"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Room setup</Label>
                        <Textarea
                          value={preferences.care_environment.room_setup}
                          onChange={(e) => updateSection('care_environment', 'room_setup', e.target.value)}
                          placeholder="Window view preferences, familiar objects, photos to display, meaningful items"
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Care Type Desired</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Pain management approach</Label>
                        <select
                          value={preferences.care_environment.pain_management}
                          onChange={(e) => updateSection('care_environment', 'pain_management', e.target.value)}
                          className="w-full border-2 rounded-md px-3 py-2 mt-1"
                        >
                          <option value="">Select...</option>
                          <option value="aggressive">Aggressive pain control</option>
                          <option value="moderate">Moderate pain control</option>
                          <option value="minimal">Minimal medication</option>
                          <option value="undecided">Not sure yet</option>
                        </select>
                      </div>
                      <div>
                        <Label>Comfort measures</Label>
                        <Textarea
                          value={preferences.care_environment.comfort_measures}
                          onChange={(e) => updateSection('care_environment', 'comfort_measures', e.target.value)}
                          placeholder="Comfort measures only vs. some treatment to extend life"
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Touch preferences</Label>
                        <Input
                          value={preferences.care_environment.touch_preferences}
                          onChange={(e) => updateSection('care_environment', 'touch_preferences', e.target.value)}
                          placeholder="Massage, hand-holding, prefer personal space"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Spiritual/religious care preferences</Label>
                        <Textarea
                          value={preferences.care_environment.spiritual_care}
                          onChange={(e) => updateSection('care_environment', 'spiritual_care', e.target.value)}
                          placeholder="Chaplain visits, prayer, sacraments, meditation"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Alternative therapies interest</Label>
                        <Input
                          value={preferences.care_environment.alternative_therapies}
                          onChange={(e) => updateSection('care_environment', 'alternative_therapies', e.target.value)}
                          placeholder="Music therapy, pet therapy, art therapy, etc."
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* Medical Wishes Tab */}
            <TabsContent value="medical-wishes">
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-6">Specific Medical Wishes</h2>
                <p className="text-sm text-gray-600 mb-6">These preferences help your care team honor your wishes during medical decision-making.</p>
                <div className="space-y-6">
                  <div>
                    <Label>DNR/DNI (Do Not Resuscitate / Do Not Intubate)</Label>
                    <Textarea
                      value={preferences.medical_wishes.dnr_dni}
                      onChange={(e) => updateSection('medical_wishes', 'dnr_dni', e.target.value)}
                      placeholder="Your wishes about CPR and intubation. Be specific about circumstances."
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Intubation wishes</Label>
                    <select
                      value={preferences.medical_wishes.intubation}
                      onChange={(e) => updateSection('medical_wishes', 'intubation', e.target.value)}
                      className="w-full border-2 rounded-md px-3 py-2 mt-1"
                    >
                      <option value="">Select preference...</option>
                      <option value="yes">Yes, willing to be intubated</option>
                      <option value="no">No intubation</option>
                      <option value="temporary">Temporary/short-term only</option>
                      <option value="depends">Depends on prognosis</option>
                    </select>
                  </div>
                  <div>
                    <Label>Feeding tube</Label>
                    <Textarea
                      value={preferences.medical_wishes.feeding_tube}
                      onChange={(e) => updateSection('medical_wishes', 'feeding_tube', e.target.value)}
                      placeholder="Preferences about artificial nutrition and hydration"
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Dialysis preferences</Label>
                    <Textarea
                      value={preferences.medical_wishes.dialysis}
                      onChange={(e) => updateSection('medical_wishes', 'dialysis', e.target.value)}
                      placeholder="Under what circumstances would you want dialysis?"
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Blood transfusions</Label>
                    <select
                      value={preferences.medical_wishes.blood_transfusion}
                      onChange={(e) => updateSection('medical_wishes', 'blood_transfusion', e.target.value)}
                      className="w-full border-2 rounded-md px-3 py-2 mt-1"
                    >
                      <option value="">Select preference...</option>
                      <option value="yes">Yes, willing to receive</option>
                      <option value="no">No transfusions</option>
                      <option value="emergency_only">Emergency situations only</option>
                    </select>
                  </div>
                  <div>
                    <Label>Resuscitation preferences</Label>
                    <Textarea
                      value={preferences.medical_wishes.resuscitation}
                      onChange={(e) => updateSection('medical_wishes', 'resuscitation', e.target.value)}
                      placeholder="Detailed preferences about CPR and other resuscitation efforts"
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Organ/tissue donation</Label>
                    <Textarea
                      value={preferences.medical_wishes.organ_donation}
                      onChange={(e) => updateSection('medical_wishes', 'organ_donation', e.target.value)}
                      placeholder="Specific organs, any organs, research purposes, etc."
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Body donation to science</Label>
                    <Textarea
                      value={preferences.medical_wishes.body_donation}
                      onChange={(e) => updateSection('medical_wishes', 'body_donation', e.target.value)}
                      placeholder="Interest in whole body donation? Specific institutions?"
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* Life Celebration Tab */}
            <TabsContent value="life-celebration">
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-4">Life Celebration Planning</h2>
                <p className="text-sm text-gray-600 mb-6">Plan a celebration WITH you while you're alive - not just for after you're gone.</p>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Event Type & Timing</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Type of celebration</Label>
                        <Input
                          value={preferences.life_celebration.event_type}
                          onChange={(e) => updateSection('life_celebration', 'event_type', e.target.value)}
                          placeholder="Party, gathering, trip, special event, concert, etc."
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Timing preferences</Label>
                        <Textarea
                          value={preferences.life_celebration.timing}
                          onChange={(e) => updateSection('life_celebration', 'timing', e.target.value)}
                          placeholder="While still mobile, before major decline, specific season, etc."
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Duration</Label>
                        <select
                          value={preferences.life_celebration.duration}
                          onChange={(e) => updateSection('life_celebration', 'duration', e.target.value)}
                          className="w-full border-2 rounded-md px-3 py-2 mt-1"
                        >
                          <option value="">Select...</option>
                          <option value="afternoon">Afternoon (2-4 hours)</option>
                          <option value="full_day">Full day</option>
                          <option value="weekend">Weekend</option>
                          <option value="week">Week-long</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Venue</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Location ideas</Label>
                        <Textarea
                          value={preferences.life_celebration.venue}
                          onChange={(e) => updateSection('life_celebration', 'venue', e.target.value)}
                          placeholder="Home, favorite restaurant, meaningful place, park, beach, etc."
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Indoor vs. outdoor</Label>
                        <select
                          value={preferences.life_celebration.indoor_outdoor}
                          onChange={(e) => updateSection('life_celebration', 'indoor_outdoor', e.target.value)}
                          className="w-full border-2 rounded-md px-3 py-2 mt-1"
                        >
                          <option value="">Select...</option>
                          <option value="indoor">Indoor</option>
                          <option value="outdoor">Outdoor</option>
                          <option value="both">Both/flexible</option>
                        </select>
                      </div>
                      <div>
                        <Label>Accessibility requirements</Label>
                        <Textarea
                          value={preferences.life_celebration.accessibility}
                          onChange={(e) => updateSection('life_celebration', 'accessibility', e.target.value)}
                          placeholder="Wheelchair access, parking, bathrooms, etc."
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Guests & Atmosphere</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Guest list / Categories</Label>
                        <Textarea
                          value={preferences.life_celebration.guest_list}
                          onChange={(e) => updateSection('life_celebration', 'guest_list', e.target.value)}
                          placeholder="Family, friends, colleagues, neighbors, church community, etc."
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Must-invite people</Label>
                        <Textarea
                          value={preferences.life_celebration.must_invite}
                          onChange={(e) => updateSection('life_celebration', 'must_invite', e.target.value)}
                          placeholder="Specific people who must be there"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Children welcome?</Label>
                        <select
                          value={preferences.life_celebration.children_welcome}
                          onChange={(e) => updateSection('life_celebration', 'children_welcome', e.target.value)}
                          className="w-full border-2 rounded-md px-3 py-2 mt-1"
                        >
                          <option value="">Select...</option>
                          <option value="yes">Yes, all ages welcome</option>
                          <option value="family_only">Family children only</option>
                          <option value="adults_only">Adults only</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Food & Beverage</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Food preferences</Label>
                        <Textarea
                          value={preferences.life_celebration.food_preferences}
                          onChange={(e) => updateSection('life_celebration', 'food_preferences', e.target.value)}
                          placeholder="Favorite foods to include, dietary considerations, cake preferences"
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Catering type</Label>
                        <select
                          value={preferences.life_celebration.catering_type}
                          onChange={(e) => updateSection('life_celebration', 'catering_type', e.target.value)}
                          className="w-full border-2 rounded-md px-3 py-2 mt-1"
                        >
                          <option value="">Select...</option>
                          <option value="catered">Professional catering</option>
                          <option value="potluck">Potluck</option>
                          <option value="mixed">Mix of both</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Entertainment & Activities</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Music</Label>
                        <Textarea
                          value={preferences.life_celebration.music}
                          onChange={(e) => updateSection('life_celebration', 'music', e.target.value)}
                          placeholder="Live band, DJ, playlist, specific songs or genres"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Activities</Label>
                        <Textarea
                          value={preferences.life_celebration.activities}
                          onChange={(e) => updateSection('life_celebration', 'activities', e.target.value)}
                          placeholder="Speeches, games, photo slideshow, video messages, memory sharing, dancing"
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Special Elements</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Theme & colors</Label>
                        <Input
                          value={preferences.life_celebration.theme_colors}
                          onChange={(e) => updateSection('life_celebration', 'theme_colors', e.target.value)}
                          placeholder="Colors, themes, dress code"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Decorations & photos</Label>
                        <Textarea
                          value={preferences.life_celebration.photos}
                          onChange={(e) => updateSection('life_celebration', 'photos', e.target.value)}
                          placeholder="Photos to display, decorations, flowers, meaningful objects"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Special elements</Label>
                        <Textarea
                          value={preferences.life_celebration.special_elements}
                          onChange={(e) => updateSection('life_celebration', 'special_elements', e.target.value)}
                          placeholder="Unique touches that represent you"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Budget range (optional)</Label>
                        <Input
                          value={preferences.life_celebration.budget}
                          onChange={(e) => updateSection('life_celebration', 'budget', e.target.value)}
                          placeholder="Estimated budget"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* Comfort & Symptom Management Tab */}
            <TabsContent value="comfort">
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-6">Comfort & Symptom Management</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Pain Management</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Pain tolerance level</Label>
                        <Textarea
                          value={preferences.comfort_symptom.pain_tolerance}
                          onChange={(e) => updateSection('comfort_symptom', 'pain_tolerance', e.target.value)}
                          placeholder="How do you handle pain? What's your comfort priority?"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Medication preferences</Label>
                        <Textarea
                          value={preferences.comfort_symptom.medication_preferences}
                          onChange={(e) => updateSection('comfort_symptom', 'medication_preferences', e.target.value)}
                          placeholder="Preferences about pain medication, concerns about sedation"
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Alternative pain relief methods</Label>
                        <Textarea
                          value={preferences.comfort_symptom.alternative_pain_relief}
                          onChange={(e) => updateSection('comfort_symptom', 'alternative_pain_relief', e.target.value)}
                          placeholder="Massage, heat, cold, positioning, meditation, etc."
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Symptom Concerns</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Specific symptom concerns</Label>
                        <Textarea
                          value={preferences.comfort_symptom.symptom_concerns}
                          onChange={(e) => updateSection('comfort_symptom', 'symptom_concerns', e.target.value)}
                          placeholder="Fears about nausea, breathlessness, confusion, anxiety, etc."
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Quality of life vs. longevity</Label>
                        <Textarea
                          value={preferences.comfort_symptom.quality_vs_longevity}
                          onChange={(e) => updateSection('comfort_symptom', 'quality_vs_longevity', e.target.value)}
                          placeholder="Your priorities when these conflict"
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Daily Comfort</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Sleep preferences</Label>
                        <Textarea
                          value={preferences.comfort_symptom.sleep_preferences}
                          onChange={(e) => updateSection('comfort_symptom', 'sleep_preferences', e.target.value)}
                          placeholder="Nap schedule, sleep aids, positioning, bedding"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Bathing preferences</Label>
                        <Textarea
                          value={preferences.comfort_symptom.bathing_preferences}
                          onChange={(e) => updateSection('comfort_symptom', 'bathing_preferences', e.target.value)}
                          placeholder="Frequency, assistance level, privacy needs"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Grooming preferences</Label>
                        <Textarea
                          value={preferences.comfort_symptom.grooming_preferences}
                          onChange={(e) => updateSection('comfort_symptom', 'grooming_preferences', e.target.value)}
                          placeholder="Hair care, shaving, makeup, nails"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Clothing preferences</Label>
                        <Input
                          value={preferences.comfort_symptom.clothing_preferences}
                          onChange={(e) => updateSection('comfort_symptom', 'clothing_preferences', e.target.value)}
                          placeholder="Comfort vs. appearance priorities"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Mobility aids</Label>
                        <Textarea
                          value={preferences.comfort_symptom.mobility_aids}
                          onChange={(e) => updateSection('comfort_symptom', 'mobility_aids', e.target.value)}
                          placeholder="Openness to wheelchairs, walkers, lifts, etc."
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* Spiritual & Emotional Care Tab */}
            <TabsContent value="spiritual">
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-6">Spiritual & Emotional Care</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Religious/Spiritual Preferences</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Religious affiliation</Label>
                        <Input
                          value={preferences.spiritual_emotional.religious_affiliation}
                          onChange={(e) => updateSection('spiritual_emotional', 'religious_affiliation', e.target.value)}
                          placeholder="Your religious or spiritual tradition"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Clergy or spiritual advisor</Label>
                        <Input
                          value={preferences.spiritual_emotional.clergy_contact}
                          onChange={(e) => updateSection('spiritual_emotional', 'clergy_contact', e.target.value)}
                          placeholder="Name and contact information"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Prayer or ritual preferences</Label>
                        <Textarea
                          value={preferences.spiritual_emotional.prayer_preferences}
                          onChange={(e) => updateSection('spiritual_emotional', 'prayer_preferences', e.target.value)}
                          placeholder="Specific prayers, rituals, or practices you'd like"
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Sacred text readings</Label>
                        <Textarea
                          value={preferences.spiritual_emotional.sacred_texts}
                          onChange={(e) => updateSection('spiritual_emotional', 'sacred_texts', e.target.value)}
                          placeholder="Specific passages, verses, or texts"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Religious objects or symbols</Label>
                        <Input
                          value={preferences.spiritual_emotional.religious_objects}
                          onChange={(e) => updateSection('spiritual_emotional', 'religious_objects', e.target.value)}
                          placeholder="Items you'd like nearby (cross, prayer beads, icons, etc.)"
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Emotional Support</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Counseling preferences</Label>
                        <Textarea
                          value={preferences.spiritual_emotional.counseling_preferences}
                          onChange={(e) => updateSection('spiritual_emotional', 'counseling_preferences', e.target.value)}
                          placeholder="Interest in therapist, chaplain, support group"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>How to support your emotional needs</Label>
                        <Textarea
                          value={preferences.spiritual_emotional.emotional_support}
                          onChange={(e) => updateSection('spiritual_emotional', 'emotional_support', e.target.value)}
                          placeholder="How you want family to support you emotionally, privacy needs for difficult moments"
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Legacy Work</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Life review/storytelling</Label>
                        <Textarea
                          value={preferences.spiritual_emotional.legacy_work}
                          onChange={(e) => updateSection('spiritual_emotional', 'legacy_work', e.target.value)}
                          placeholder="Interest in recording life stories, ethical will, legacy letters"
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Messages to record</Label>
                        <Textarea
                          value={preferences.spiritual_emotional.messages_to_record}
                          onChange={(e) => updateSection('spiritual_emotional', 'messages_to_record', e.target.value)}
                          placeholder="Video, audio, or written messages for loved ones, future events, etc."
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* Information Sharing Tab */}
            <TabsContent value="sharing">
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-6">Information Sharing Preferences</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-3">What to Share with Whom</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Core team information level</Label>
                        <Textarea
                          value={preferences.information_sharing.core_team_info}
                          onChange={(e) => updateSection('information_sharing', 'core_team_info', e.target.value)}
                          placeholder="What your inner circle should know (usually everything)"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Support team information level</Label>
                        <Textarea
                          value={preferences.information_sharing.support_team_info}
                          onChange={(e) => updateSection('information_sharing', 'support_team_info', e.target.value)}
                          placeholder="What extended family/friends should know"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>What children/grandchildren should know</Label>
                        <Textarea
                          value={preferences.information_sharing.children_info}
                          onChange={(e) => updateSection('information_sharing', 'children_info', e.target.value)}
                          placeholder="Age-appropriate information for young family members"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Social media/public information</Label>
                        <Textarea
                          value={preferences.information_sharing.social_media}
                          onChange={(e) => updateSection('information_sharing', 'social_media', e.target.value)}
                          placeholder="What can be shared publicly, what should stay private"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Communication Preferences</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>How updates should be shared</Label>
                        <Textarea
                          value={preferences.information_sharing.update_frequency}
                          onChange={(e) => updateSection('information_sharing', 'update_frequency', e.target.value)}
                          placeholder="Frequency, method (group text, email, phone tree)"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Who speaks on your behalf</Label>
                        <Input
                          value={preferences.information_sharing.spokesperson}
                          onChange={(e) => updateSection('information_sharing', 'spokesperson', e.target.value)}
                          placeholder="Designated spokesperson for medical/care updates"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Visiting hours preferences</Label>
                        <Textarea
                          value={preferences.information_sharing.visiting_hours}
                          onChange={(e) => updateSection('information_sharing', 'visiting_hours', e.target.value)}
                          placeholder="When you want visitors, quiet times, limits on visit length"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </TabsContent>

            {/* Providers & Hospice Tab */}
            <TabsContent value="providers">
              <Card className="p-6">
                <h2 className="text-2xl font-bold mb-6">Provider & Hospice Preferences</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Hospice Selection</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Preferred hospice organizations</Label>
                        <Textarea
                          value={preferences.providers.preferred_hospice}
                          onChange={(e) => updateSection('providers', 'preferred_hospice', e.target.value)}
                          placeholder="Names of hospice organizations you've researched or prefer"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Hospice type preference</Label>
                        <select
                          value={preferences.providers.hospice_type}
                          onChange={(e) => updateSection('providers', 'hospice_type', e.target.value)}
                          className="w-full border-2 rounded-md px-3 py-2 mt-1"
                        >
                          <option value="">Select...</option>
                          <option value="home">Home hospice</option>
                          <option value="facility">Hospice facility</option>
                          <option value="either">Either is acceptable</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Care Team Composition</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Preferred physicians</Label>
                        <Textarea
                          value={preferences.providers.preferred_physicians}
                          onChange={(e) => updateSection('providers', 'preferred_physicians', e.target.value)}
                          placeholder="Doctors you want involved in your care"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Care team preferences</Label>
                        <Textarea
                          value={preferences.providers.care_team}
                          onChange={(e) => updateSection('providers', 'care_team', e.target.value)}
                          placeholder="Preferred nurses, aides, therapists, volunteers"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold mb-3">Backup Care Plans</h3>
                    <div className="space-y-4">
                      <div>
                        <Label>Backup caregiver plan</Label>
                        <Textarea
                          value={preferences.providers.backup_caregiver}
                          onChange={(e) => updateSection('providers', 'backup_caregiver', e.target.value)}
                          placeholder="If primary caregiver is unavailable, respite care options"
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Emergency contacts & hospital preferences</Label>
                        <Textarea
                          value={preferences.providers.emergency_contacts}
                          onChange={(e) => updateSection('providers', 'emergency_contacts', e.target.value)}
                          placeholder="Emergency contacts, preferred hospital if crisis care needed"
                          rows={3}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        )}

        <div className="mt-8 flex justify-center">
          <Button onClick={savePreferences} disabled={saving} size="lg" className="bg-blue-600 hover:bg-blue-700">
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Saving...' : 'Save All Preferences'}
          </Button>
        </div>

        <div className="mt-8">
          <QuickActions position="bottom" />
        </div>
      </div>
    </Layout>
  );
};

export default EndOfLife;
